import { Suspense, memo } from 'react';
import { useActiveContext } from '@/shared/hooks/useActiveContext';
import * as SidebarLayouts from '@/shared/ui/layouts/sidebar-layouts';
import { useSidebarState } from '@/shared/ui/layouts/sidebar-layouts';
import { ContextSwitcherHorizontal } from '@/shared/ui/components/navigation/ContextSwitcherHorizontal';

/**
 * ContextSidebar
 *
 * Replaces the direct AppSidebar mounting in AppLayout.
 * Reads the active context from useActiveContext(), then renders
 * the appropriate SidebarContent from the contextRegistry.
 *
 * Each context's SidebarContent is lazy-loaded — the Suspense boundary
 * shows a skeleton while the chunk is being fetched.
 *
 * The SidebarLayouts.Root wrapper manages the fold/unfold state and
 * the sidebar's physical width — this is shared across all context sidebars.
 */
export const ContextSidebar = memo(function ContextSidebar() {
  const { activeContextConfig } = useActiveContext();
  const { folded, setFolded } = useSidebarState();
  const { SidebarContent } = activeContextConfig;

  return (
    <SidebarLayouts.Root folded={folded} onFoldChange={setFolded} foldable>
      <ContextSwitcherHorizontal />
      <Suspense fallback={<SidebarLoadingSkeleton />}>
        <SidebarContent />
      </Suspense>
    </SidebarLayouts.Root>
  );
});

// ─── Loading Skeleton ────────────────────────────────────────────────────────

/**
 * SidebarLoadingSkeleton
 *
 * Shown while the context sidebar chunk is being lazy-loaded.
 * Simulates the typical sidebar structure to avoid layout shift.
 */
function SidebarLoadingSkeleton() {
  return (
    <div
      className="flex flex-col gap-2 px-3 pt-4 pb-3"
      data-testid="ContextSidebar/loading"
      aria-label="Loading sidebar"
    >
      {/* Header area skeleton */}
      {[36, 36, 36, 36].map((height, i) => (
        <div
          key={`header-${i}`}
          style={{ height }}
          className="rounded-lg bg-background-tint-02 animate-pulse opacity-60 w-full"
        />
      ))}

      {/* Divider */}
      <div className="my-2 border-t border-border-01 opacity-30" />

      {/* Body skeleton — varying widths for natural look */}
      {[80, 65, 75, 55, 70, 80, 60].map((widthPct, i) => (
        <div
          key={`body-${i}`}
          style={{ height: 28, width: `${widthPct}%` }}
          className="rounded bg-background-tint-02 animate-pulse opacity-40"
        />
      ))}
    </div>
  );
}
